import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Env, getAdminApiUrl, ENV_COLORS } from '@/pages/Admin/shared';
import { useNotificationMetrics, WeeksFilter } from './api/useNotificationMetrics';
import SummaryCards from './components/SummaryCards';
import WeeklyTrendChart from './components/WeeklyTrendChart';
import BusinessBreakdownTable from './components/BusinessBreakdownTable';
import AreaBroadcastsSection from './components/AreaBroadcastsSection';

const WEEK_OPTIONS: Array<{ value: WeeksFilter; label: string }> = [
  { value: 'all', label: 'All time' },
  { value: 4, label: '4 weeks (≈30d)' },
  { value: 8, label: '8 weeks' },
  { value: 12, label: '12 weeks' },
];

function exportCsv(data: ReturnType<typeof useNotificationMetrics>['data'], weeks: WeeksFilter) {
  const rows: string[] = ['Business Name,Week Start,Sent,Opened,Open Rate,Attributed Visits'];

  for (const biz of data.by_business) {
    for (const w of biz.weekly) {
      rows.push(
        [biz.business_name, w.week_start, w.total_sent, w.total_opened, `${w.open_rate}%`, w.attributed_visits].join(','),
      );
    }
  }

  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `notification-metrics-${weeks === 'all' ? 'all-time' : `${weeks}w`}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function NotificationMetricsPage(): React.ReactElement {
  // 4 weeks is this page's nearest equivalent of the 30-day default the other
  // analytics pages open on; the data only exists in weekly buckets.
  const [weeks, setWeeks] = useState<WeeksFilter>(4);
  const [selectedEnv, setSelectedEnv] = useState<Env>('dev');
  const activeApiUrl = getAdminApiUrl(selectedEnv);
  const { data, loading, error, refetch } = useNotificationMetrics(weeks, activeApiUrl);

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <div className="max-w-6xl mx-auto px-6 pt-8 pb-16">

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link to="/b2b" className="text-sm text-gray-500 hover:text-gray-300 transition-colors no-underline">
            B2B Dashboards
          </Link>
          <span className="text-gray-700 mx-2">/</span>
          <span className="text-sm text-gray-400">Notification Performance</span>
        </div>

        {/* Header row */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-semibold text-white font-display tracking-tight">Notification Performance</h1>

            {/* Env switcher */}
            <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
              {(['dev', 'staging', 'prod'] as Env[]).map((env) => {
                const envApiUrl = getAdminApiUrl(env);
                const isConfigured = Boolean(envApiUrl);
                const isActive = selectedEnv === env;
                return (
                  <button
                    key={env}
                    onClick={() => isConfigured && setSelectedEnv(env)}
                    disabled={!isConfigured}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all cursor-pointer ${
                      isActive
                        ? ENV_COLORS[env].pill
                        : isConfigured
                          ? 'border-transparent text-gray-500 hover:text-gray-300 hover:bg-gray-700/50'
                          : 'border-transparent text-gray-700 cursor-not-allowed'
                    }`}
                  >
                    {env.toUpperCase()}
                  </button>
                );
              })}
            </div>

            {/* Prod warning badge */}
            {selectedEnv === 'prod' && (
              <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
                PRODUCTION
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-500">Weekly totals, last</span>
            <select
              value={String(weeks)}
              onChange={(e) => setWeeks(e.target.value === 'all' ? 'all' : Number(e.target.value))}
              className="bg-gray-800 border border-gray-700 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-violet-500 cursor-pointer"
            >
              {WEEK_OPTIONS.map((opt) => (
                <option key={String(opt.value)} value={String(opt.value)}>
                  {opt.label}
                </option>
              ))}
            </select>
            <button
              onClick={() => exportCsv(data, weeks)}
              disabled={loading || data.by_business.length === 0}
              className="px-4 py-2 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:bg-gray-700 hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer"
            >
              Export CSV
            </button>
          </div>
        </div>

        {/* Error state */}
        {error && (
          <div className="mb-8 bg-red-950/30 border border-red-800/40 rounded-xl p-4 flex items-center justify-between">
            <p className="text-sm text-red-400">{error}</p>
            <button
              onClick={refetch}
              className="px-3 py-1.5 text-sm font-medium text-red-300 bg-red-900/40 rounded-lg hover:bg-red-900/60 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && data.weekly.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-2xl p-12 text-center">
            <svg className="w-12 h-12 text-gray-700 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1">
              <path strokeLinecap="round" strokeLinejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75v-.7V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0" />
            </svg>
            <p className="text-gray-400 text-sm">No notification data available for this period.</p>
            <p className="text-gray-600 text-xs mt-1">Data appears after the weekly aggregation runs on Monday.</p>
          </div>
        )}

        {/* Dashboard content */}
        {(loading || data.weekly.length > 0) && (
          <div className="space-y-6">
            <SummaryCards data={data} loading={loading} />
            <WeeklyTrendChart weekly={data.weekly} loading={loading} />
            <BusinessBreakdownTable businesses={data.by_business} loading={loading} />
          </div>
        )}

        {/* Area Broadcasts — independent of per-business notification metrics,
            so render regardless of the metrics empty state. */}
        <div className={(loading || data.weekly.length > 0) ? 'mt-6' : 'mt-4'}>
          <AreaBroadcastsSection env={selectedEnv} />
        </div>
      </div>
    </div>
  );
}

export default NotificationMetricsPage;
